import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, BadgeCheck, Plus, Trash2, Calendar, Upload, X, ShieldAlert, Shirt, ImageIcon } from "lucide-react";

const PRODUCT_TYPES = [
    { id: "vestido_15", label: "Vestido de 15 años" },
    { id: "vestido_graduacion", label: "Vestido de graduación" },
    { id: "vestido_novia", label: "Vestido de novia" },
    { id: "traje", label: "Traje / Esmoquin" },
    { id: "vestido_gala", label: "Vestido de gala / Coctel" },
    { id: "otro", label: "Otro (especificar en notas)" },
];

const blankItem = () => ({
    type: "vestido_15", name: "", size: "", color: "",
    estimated_price: "", reference_images: [], notes: "",
});

const minDateStr = () => {
    const d = new Date();
    d.setDate(d.getDate() + 61);
    return d.toISOString().slice(0, 10);
};

const fileToDataURL = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
});

const compressImage = async (file) => {
    if (file.size < 200 * 1024) return fileToDataURL(file);
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            const maxW = 1200;
            const scale = Math.min(1, maxW / img.width);
            canvas.width = img.width * scale;
            canvas.height = img.height * scale;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/jpeg", 0.8));
        };
        img.onerror = () => fileToDataURL(file).then(resolve);
        img.src = URL.createObjectURL(file);
    });
};

export default function SolicitarProducto() {
    const { user, loading } = useAuth();
    const nav = useNavigate();
    const [items, setItems] = useState([blankItem()]);
    const [neededDate, setNeededDate] = useState(minDateStr());
    const [phone, setPhone] = useState("");
    const [generalNotes, setGeneralNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (user?.phone) setPhone(user.phone); }, [user]);

    if (loading) return <div className="p-12 text-center text-[var(--tb-text-2)]">Cargando...</div>;

    // Gate 1: not logged in
    if (!user) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-16 text-center fade-in">
                <Sparkles className="w-12 h-12 text-[var(--tb-gold)] mx-auto mb-4" />
                <h1 className="font-display text-3xl mb-3">Solicitar Producto</h1>
                <p className="text-[var(--tb-text-2)] mb-6">Para solicitar un producto a medida necesitas tener una cuenta verificada.</p>
                <Link to="/login?next=/solicitar-producto" className="tb-btn-primary" data-testid="sp-login-cta">Acceder o registrarme</Link>
            </div>
        );
    }

    // Gate 2: not verified
    if (!user.verified) {
        return (
            <div className="max-w-2xl mx-auto px-6 py-16 fade-in">
                <div className="tb-card p-8 text-center">
                    <ShieldAlert className="w-12 h-12 text-[var(--tb-gold)] mx-auto mb-4" />
                    <h1 className="font-display text-3xl mb-3">Verifica tu cuenta primero</h1>
                    <p className="text-[var(--tb-text-2)] mb-6">
                        Solo usuarios con cuenta verificada <BadgeCheck className="inline w-4 h-4 text-blue-500" /> pueden solicitar productos a medida.
                        Sube tu identificación desde tu perfil para iniciar la verificación.
                    </p>
                    <Link to="/perfil" className="tb-btn-primary" data-testid="sp-verify-cta">Ir a mi perfil</Link>
                </div>
            </div>
        );
    }

    const upd = (i, k, v) => {
        const arr = [...items];
        arr[i] = { ...arr[i], [k]: v };
        setItems(arr);
    };

    const addItem = () => {
        if (items.length >= 5) {
            toast.info("Máximo 5 prendas por solicitud. Podrás negociar más durante el proceso.");
            return;
        }
        setItems([...items, blankItem()]);
    };

    const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

    const onPickImages = async (i, files) => {
        const arr = Array.from(files).slice(0, 4 - (items[i].reference_images?.length || 0));
        if (!arr.length) return;
        try {
            const dataUrls = await Promise.all(arr.map(compressImage));
            const next = [...(items[i].reference_images || []), ...dataUrls].slice(0, 4);
            upd(i, "reference_images", next);
        } catch {
            toast.error("Error al procesar las imágenes");
        }
    };

    const removeImage = (i, j) => {
        const arr = [...(items[i].reference_images || [])];
        arr.splice(j, 1);
        upd(i, "reference_images", arr);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (items.some((it) => !it.name.trim())) {
            toast.error("Cada prenda necesita un nombre/descripción");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                customer_phone: phone,
                needed_date: neededDate,
                general_notes: generalNotes,
                items: items.map((it) => ({
                    ...it,
                    estimated_price: parseFloat(it.estimated_price || 0) || 0,
                })),
            };
            const { data } = await api.post("/product-requests", payload);
            toast.success("Solicitud enviada: " + data._id);
            nav("/perfil?tab=solicitudes");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al enviar solicitud");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="max-w-4xl mx-auto px-6 py-10 fade-in">
            <div className="flex items-start gap-4 mb-6">
                <Sparkles className="w-10 h-10 text-[var(--tb-gold)] shrink-0 mt-1" />
                <div>
                    <h1 className="font-display text-4xl mb-1">Solicitar Producto</h1>
                    <p className="text-[var(--tb-text-2)] text-sm">Buscamos por ti en el extranjero. Vestidos de 15, graduación, novia, trajes y más — traídos especialmente para tu evento.</p>
                </div>
            </div>

            {/* Reglas */}
            <div className="tb-card p-5 mb-6 bg-[var(--tb-border-gold)]/10">
                <div className="tb-overline text-[var(--tb-gold)] mb-2">Cómo funciona</div>
                <ul className="text-sm space-y-1.5 text-[var(--tb-text)]">
                    <li>✦ <span className="font-medium">Anticipación mínima:</span> 2 meses (60 días) antes de la fecha de uso.</li>
                    <li>✦ <span className="font-medium">Hasta 5 prendas</span> por solicitud (podemos negociar más durante el proceso).</li>
                    <li>✦ <span className="font-medium">Envío totalmente gratis</span> dentro de El Salvador.</li>
                    <li>✦ Los precios pueden variar según tela, complejidad y tiempo. Te cotizaremos antes de proceder.</li>
                    <li>✦ Una vez enviada, se abrirá un chat dedicado donde discutiremos cada detalle contigo.</li>
                </ul>
            </div>

            <form onSubmit={submit} className="space-y-6">
                {/* Contacto */}
                <div className="tb-card p-6">
                    <div className="tb-overline text-[var(--tb-gold)] mb-3">Datos de contacto</div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="tb-overline">Teléfono / WhatsApp *</label>
                            <input className="tb-input" required placeholder="+503 7000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="sp-phone" />
                        </div>
                        <div>
                            <label className="tb-overline">Fecha necesaria *</label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--tb-muted)] pointer-events-none" />
                                <input type="date" className="tb-input !pl-9" required min={minDateStr()} value={neededDate} onChange={(e) => setNeededDate(e.target.value)} data-testid="sp-date" />
                            </div>
                            <div className="text-xs text-[var(--tb-muted)] mt-1">Mínimo {minDateStr()}.</div>
                        </div>
                    </div>
                </div>

                {/* Items */}
                <div className="space-y-4">
                    {items.map((it, i) => (
                        <div key={`item-${i}`} className="tb-card p-6" data-testid={`sp-item-${i}`}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="tb-overline text-[var(--tb-gold)] flex items-center gap-2">
                                    <Shirt className="w-4 h-4" /> Prenda {i + 1}
                                </div>
                                {items.length > 1 && (
                                    <button type="button" onClick={() => removeItem(i)} className="text-red-500 text-xs flex items-center gap-1 hover:underline" data-testid={`sp-remove-${i}`}>
                                        <Trash2 className="w-3.5 h-3.5" /> Eliminar
                                    </button>
                                )}
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <label className="tb-overline">Tipo de prenda *</label>
                                    <select className="tb-input" value={it.type} onChange={(e) => upd(i, "type", e.target.value)} data-testid={`sp-type-${i}`}>
                                        {PRODUCT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="tb-overline">Nombre / descripción corta *</label>
                                    <input className="tb-input" required placeholder="Ej. Vestido princesa con corset" value={it.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`sp-name-${i}`} />
                                </div>
                                <div>
                                    <label className="tb-overline">Talla</label>
                                    <input className="tb-input" placeholder="XS, S, M, L o medidas" value={it.size} onChange={(e) => upd(i, "size", e.target.value)} data-testid={`sp-size-${i}`} />
                                </div>
                                <div>
                                    <label className="tb-overline">Color preferido</label>
                                    <input className="tb-input" placeholder="Rosa pastel, beige, etc." value={it.color} onChange={(e) => upd(i, "color", e.target.value)} data-testid={`sp-color-${i}`} />
                                </div>
                                <div>
                                    <label className="tb-overline">Precio estimado (USD)</label>
                                    <input type="number" step="0.01" min="0" className="tb-input" placeholder="250.00" value={it.estimated_price} onChange={(e) => upd(i, "estimated_price", e.target.value)} data-testid={`sp-price-${i}`} />
                                    <div className="text-xs text-[var(--tb-muted)] mt-1">Te cotizaremos el precio final.</div>
                                </div>
                                <div>
                                    <label className="tb-overline">Notas / preferencias</label>
                                    <input className="tb-input" placeholder="Tela, escote, mangas, accesorios..." value={it.notes} onChange={(e) => upd(i, "notes", e.target.value)} data-testid={`sp-notes-${i}`} />
                                </div>
                            </div>

                            {/* Reference images */}
                            <div className="mt-4">
                                <label className="tb-overline flex items-center gap-2"><ImageIcon className="w-3.5 h-3.5" /> Imágenes de referencia (hasta 4)</label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {(it.reference_images || []).map((src, j) => (
                                        <div key={`img-${i}-${j}`} className="relative w-20 h-20 group">
                                            <img src={src} alt="ref" className="w-20 h-20 object-cover rounded border border-[var(--tb-border)]" />
                                            <button type="button" onClick={() => removeImage(i, j)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition">
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                    {(it.reference_images?.length || 0) < 4 && (
                                        <label className="w-20 h-20 border-2 border-dashed border-[var(--tb-border)] rounded flex flex-col items-center justify-center cursor-pointer hover:border-[var(--tb-gold)] text-[var(--tb-muted)] text-[10px] gap-1">
                                            <Upload className="w-4 h-4" />
                                            <span>Subir</span>
                                            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPickImages(i, e.target.files)} data-testid={`sp-upload-${i}`} />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    <button type="button" onClick={addItem} disabled={items.length >= 5} className="tb-btn-secondary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="sp-add-item">
                        <Plus className="w-4 h-4" /> Agregar otra prenda ({items.length}/5)
                    </button>
                </div>

                {/* Notas generales */}
                <div className="tb-card p-6">
                    <div className="tb-overline text-[var(--tb-gold)] mb-3">Notas adicionales (opcional)</div>
                    <textarea className="tb-input min-h-[100px]" placeholder="Detalles del evento, color de ambientación, preferencias estéticas generales..." value={generalNotes} onChange={(e) => setGeneralNotes(e.target.value)} data-testid="sp-general-notes" />
                </div>

                <button type="submit" disabled={submitting} className="tb-btn-primary w-full py-4 text-base disabled:opacity-50" data-testid="sp-submit">
                    {submitting ? "Enviando..." : "Enviar solicitud"}
                </button>
            </form>
        </div>
    );
}
